import {
  ErrorCodes,
  createError,
  Error,
  Logger,
  HttpRequest,
  createRequestBuilder,
  createHttpClient,
} from '@quickplay/foundation';
import { AdMetadata } from '../sidebandMetadata/model/TritonStreamSidebandMetadata';
import { loadVASTCompanionAd } from './Util/CompanionBannerUtil';
import { VASTParser } from './Util/VASTParser';
import { fetchVASTDocument } from './Util/VASTUtil';
import { XMLParser } from './Util/XmlParser';
import { CompanionAd } from './model/CompanionAd';
import { CompanionBannerModule } from './model/CompanionModule';
import { VASTAd, Creative } from './model/VASTAdTypes';
import { TritonErrorCodes } from '../internal/TritonErrorCodes';

export function createCompanionAdModule(logger?: Logger): CompanionBannerModule {
  const vastCompanionPriorites = ['static', 'iframe', 'html'];
  let syncedBannerContainers: Element[] = [];
  let companionBannerErrorCallback: ((error: Error) => void) | undefined;
  let companionBannerReadyCallback: ((syncedBannerIdList: Element[]) => void) | undefined;

  /**
   * parses vast xml file.
   * @param adMetadata AdMetadata
   * @returns Promise
   */
  async function parseVast(adMetadata: AdMetadata): Promise<VASTAd | undefined> {
    const adParser = new VASTParser();
    if (adMetadata.vastInline !== undefined) {
      const VASTXml = XMLParser.parse(adMetadata.vastInline);
      if (VASTXml) {
        return adParser.parse(VASTXml.documentElement);
      }
    } else if (adMetadata.vastRemote !== undefined) {
      try {
        const response = await fetchVASTDocument(adMetadata.vastRemote);
        const VASTXml: Document | undefined = XMLParser.parse(response);
        if (VASTXml) {
          return adParser.parse(VASTXml.documentElement);
        }
      } catch (error) {
        return Promise.reject(
          createError(
            TritonErrorCodes.AD_VAST_DOCUMENT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
            'Failed to fetch VAST document',
            error as Error,
          ),
        );
      }
    }
    return Promise.reject(
      createError(
        TritonErrorCodes.AD_VAST_DOCUMENT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
        'Vast information is not available',
      ),
    );
  }

  /**
   * return companion ads by parsing using vast information provided.
   * @param vastAd vast information of the ad parsed from vastXML.
   * @returns CompanionAd[] | undefined
   */
  function getCompanionAds(vastAd: VASTAd): CompanionAd[] | undefined {
    let vastCompanionAds: CompanionAd[] | undefined = undefined;
    if (vastAd && vastAd.inlineAd) {
      vastAd.inlineAd.creatives?.forEach((creative: Creative) => {
        if (creative.companionAds) {
          vastCompanionAds = creative.companionAds;
        }
      });
    }
    return vastCompanionAds;
  }

  /**
   * Filters companion ad based on the container size provided.
   * @param companionAds Companion Ad information
   * @returns CompanionAd[]
   */
  function filterCompanionsOnPriorities(companionAds: CompanionAd[]): CompanionAd[] {
    if (!companionAds || !companionAds.length) {
      return companionAds;
    }

    const companions: CompanionAd[] = [];
    const size: number[] = [];
    vastCompanionPriorites.forEach(priority => {
      {
        companionAds.forEach((companionAd: CompanionAd) => {
          const companionAdSize = companionAd.width + companionAd.height;
          if (size.indexOf(companionAdSize) < 0 && companionAd.resourceType === priority) {
            size.push(companionAdSize);
            companions.push(companionAd);
          }
        });
      }
    });
    return companions;
  }

  /**
   * returns array of HTML Elements that are suitable to render companion ads.
   * @param companionAds Array of companion ad.
   * @param containers Array of HTML container used to render companion banner.
   * @returns Element[]
   */
  function loadCompanions(companionAds: CompanionAd[], containers: Element[]): Element[] {
    const companions: CompanionAd[] = filterCompanionsOnPriorities(companionAds);
    const syncedBannersList: Element[] = [];
    if (!containers.length) {
      return [];
    }
    containers.forEach((container: Element) => {
      companions.forEach(ad => {
        if (container !== null && ad.width === container.clientWidth && ad.height === container.clientHeight) {
          loadVASTCompanionAd(container, ad);
          addCompanionClickTracking(container, ad);
          callImpressions(ad);
          syncedBannersList.push(container);
        }
      });
    });
    return syncedBannersList;
  }

  /**
   * handles companion banner click tracking events.
   * @param container Element
   * @param companion CompanionAd
   */
  function addCompanionClickTracking(container: Element, companion: CompanionAd): void {
    container.addEventListener('click', async () => {
      if (companion.clickTrackingURL) {
        const request: HttpRequest<any> = createRequestBuilder().url(companion.clickTrackingURL).get().build();
        const httpClient = createHttpClient<any, string>();
        httpClient.callWith(request).catch((error: any) => {
          logger?.debug('Ad Click tracking call has failed: ', error);
        });
      }
    });
  }

  function sendCompanionBannerReady(containers: Element[]): void {
    companionBannerReadyCallback && companionBannerReadyCallback(containers);
  }

  /**
   * handles impresssion trackings.
   * @param companionAd CompanionAd
   */
  function callImpressions(companionAd: CompanionAd): void {
    if (companionAd.creativeView && companionAd.creativeView.length !== 0) {
      companionAd.creativeView.forEach(async (impression: string) => {
        const request: HttpRequest<any> = createRequestBuilder().url(impression).get().build();
        const httpClient = createHttpClient<any, string>();
        httpClient.callWith(request).catch((error: any) => {
          logger?.debug('Ad impression tracking call has failed: ', error);
        });
      });
    }
  }

  function sendCompanionBannerError(error: Error): void {
    companionBannerErrorCallback && companionBannerErrorCallback(error);
  }

  return {
    showCompanions(
      adCuepointData: AdMetadata,
      companionBannerIdList: Element[],
      onCompanionBannerReady?: (syncedBannerIdList: Element[]) => void,
      onCompanionBannerError?: (error: Error) => void,
    ): void {
      if (onCompanionBannerError) {
        companionBannerErrorCallback = onCompanionBannerError;
      }
      if (onCompanionBannerReady) {
        companionBannerReadyCallback = onCompanionBannerReady;
      }

      parseVast(adCuepointData)
        .then((vastAd: VASTAd | undefined) => {
          if (vastAd !== undefined) {
            const companions: CompanionAd[] | undefined = getCompanionAds(vastAd);
            if (companions) {
              const syncedBanners: Element[] = loadCompanions(companions, companionBannerIdList);
              syncedBannerContainers = syncedBanners;
              syncedBanners.length !== 0
                ? sendCompanionBannerReady(syncedBannerContainers)
                : logger?.debug('Companion banner size is not matching with provided container size');
            } else {
              sendCompanionBannerError(
                createError(
                  TritonErrorCodes.COMPANION_AD_BANNER_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
                  'Companions are not available',
                ),
              );
            }
          } else {
            sendCompanionBannerError(
              createError(
                TritonErrorCodes.AD_VAST_DOCUMENT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
                'VAST document parsing failed',
              ),
            );
          }
        })
        .catch((error: Error) => {
          sendCompanionBannerError(error);
        });
    },

    hideCompanions(): void {
      syncedBannerContainers.forEach((container: Element) => {
        if (container && container.childNodes) {
          container.removeEventListener('click', (_event: any) => {
            // no_op
          });
          try {
            container.innerHTML = '';
          } catch {}
        }
      });
      syncedBannerContainers = [];
    },
  };
}
